import { X } from 'lucide-react'
import { type ReactNode, useEffect, useRef } from 'react'
import { IconButton } from './ui'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** Wider panel on larger screens (level grids). */
  wide?: boolean
}

/**
 * Native <dialog> that slides up as a bottom sheet on phones and sits as a centered panel from
 * `sm:`. Escape and the close button close it; the browser traps focus.
 */
export function BottomSheet({ open, onClose, title, children, wide = false }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  // A close we trigger ourselves (the parent switched sheets) must not report back as a dismissal,
  // or the parent would clear the sheet it just opened.
  const silent = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) {
      silent.current = true
      el.close()
    }
  }, [open])

  const onDialogClose = () => {
    if (silent.current) {
      silent.current = false
      return
    }
    onClose()
  }

  return (
    <dialog
      ref={ref}
      onClose={onDialogClose}
      className={`mt-auto mb-0 max-h-[85dvh] w-full max-w-none rounded-t-3xl bg-white p-0 text-slate-900 shadow-2xl backdrop:bg-slate-900/60 open:animate-sheet-up dark:bg-slate-900 dark:text-slate-100 sm:my-auto sm:rounded-3xl ${wide ? 'sm:max-w-2xl' : 'sm:max-w-md'}`}
    >
      <div className="flex max-h-[85dvh] flex-col">
        <div
          className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-slate-300 dark:bg-slate-700 sm:hidden"
          aria-hidden
        />
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
          <h2 className="font-bold">{title}</h2>
          <IconButton label="Close" onClick={onClose}>
            <X className="size-5" aria-hidden />
          </IconButton>
        </div>
        <div className="overflow-y-auto p-4">{children}</div>
      </div>
    </dialog>
  )
}
